import { TranslationKey } from '@Localization';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { ConfirmationModal } from '../../../components/ConfirmationModal';
import { InputStatus } from '../../../models/error.model';
import { IPagingInfoWithOrder } from '../../../models/paginationInfo.model';
import { ListViewMode } from '../../user/user.store';
import { RenameImageModal } from '../containers/RenameImageModal';
import { IDatasetDetailsImage } from '../datasetsDetails.store';
import { DatasetDetailsImagesListTable } from './DatasetDetailsImagesListTable';
import { DatasetDetailsImagesListThumbs } from './DatasetDetailsImagesListThumbs';

export interface IDatasetDetailsImagesProps {
  workspaceId: string;
  datasetId: string;
  datasetImages: IDatasetDetailsImage[];
  isLoading: boolean;
  showDeleteConfirmationModal: boolean;
  imageIdToDeleteWasAnnotated: boolean;
  imageIdToDeleteIsLocked: boolean;
  paging: IPagingInfoWithOrder;
  isImageDownloadable: boolean;
  disabled: boolean;
  viewMode: ListViewMode;
  authToken: string;
  isUploading: boolean;
  imagesToBulkDelete: string[];
  showRenameImageConfirmationModal: boolean;
  renameImageName: string;
  renameImageInputStatus: InputStatus;
  onImageDownload: (id: string) => Promise<void>;
  onImageDeleted: (id: string) => void;
  onCancelImageDelete: () => void;
  onConfirmImageDelete: () => Promise<void>;
  onPaginationChange: (pageNumber: number, pageSize: number) => Promise<void>;
  onOrderingChange: (orderBy: string, orderType: string) => Promise<void>;
  onThumbCheck: (id: string, isBatch: boolean) => void;
  toggleRenameImageModal: (imageName: string) => void;
  renameImage: () => Promise<void>;
  handleRenameImageNameChange: (value: string) => void;
  onRenameImage: (id: string) => void;
  onSelectAll: (isSelected: boolean) => void;
}

export const DatasetDetailsImagesList = observer((props: IDatasetDetailsImagesProps): ReactElement => {
  const toggleRenameImageModalWithEmpty = (): void => {
    props.toggleRenameImageModal('');
  };

  let message: TranslationKey = 'image_delete_confirmation_message';

  if (props.imageIdToDeleteWasAnnotated) {
    message = 'remove_this_image_has_already_been_annotated';
  }
  if (props.imageIdToDeleteIsLocked) {
    message = 'remove_this_image_is_in_use';
  }

  const isLoading = props.isLoading || props.isUploading;

  return (
    <div className='tab-content'>
      {props.viewMode === ListViewMode.List && (
        <DatasetDetailsImagesListTable
          {...props}
          isLoading={isLoading}
        />
      )}
      {props.viewMode === ListViewMode.Tiles && (
        <DatasetDetailsImagesListThumbs
          {...props}
          isLoading={isLoading}
        />
      )}
      <ConfirmationModal
        showModal={props.showDeleteConfirmationModal}
        onCancel={props.onCancelImageDelete}
        onConfirm={props.onConfirmImageDelete}
        confirmationHeader='image_delete_confirmation_header'
        confirmationQuestion={message}
        confirmationYes='delete'
      />
      <RenameImageModal
        showRenameImage={props.showRenameImageConfirmationModal}
        renameImageName={props.renameImageName}
        renameImageNameStatus={props.renameImageInputStatus}
        toggleRenameImage={toggleRenameImageModalWithEmpty}
        onNameChange={props.handleRenameImageNameChange}
        onRenameSave={props.renameImage}
      />
    </div>
  );
});
